import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API_BASE } from "../config";
import { Search, ChevronLeft, ChevronRight, Loader2 } from "lucide-react";

interface Investment {
  _id: string;
  email: string;
  totalAmount: string;
  subscription: string;
  incomeDate: string;
  createdAt: string;
  isClosed: boolean;
}

const Invest = () => {
  const [investmentData, setInvestmentData] = useState<Investment[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0
  });

  const [debitValues, setDebitValues] = useState<Record<string, string>>({});
  const [creditValues, setCreditValues] = useState<Record<string, string>>({});

  const fetchInvestments = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_BASE}/api/invest/admin/invests`, {
        params: {
          page: pagination.page,
          limit: pagination.limit,
          search: searchTerm
        }
      });

      if (response.data.status === "success") {
        setInvestmentData(response.data.data);
        setPagination(prev => ({
          ...prev,
          total: response.data.pagination.total,
          totalPages: response.data.pagination.totalPages
        }));
      }
    } catch (error) {
      console.error("Error fetching investments:", error);
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.limit, searchTerm]);

  useEffect(() => {
    const delaySearch = setTimeout(() => {
      fetchInvestments();
    }, 500);
    return () => clearTimeout(delaySearch);
  }, [fetchInvestments]);

  const handleTransaction = async (email: string, amount: string, incomeDate: string, type: "debit" | "credit") => {
    if (!amount || isNaN(Number(amount))) return;
    try {
      const amountNum = parseFloat(amount);
      await axios.post(`${API_BASE}/api/invest/updateInvest`, {
        email,
        incomeDate,
        amount: amountNum.toFixed(2),
        type,
        Subscription: "Basic"
      });

      fetchInvestments();

      if (type === "debit") setDebitValues(prev => ({ ...prev, [email]: "" }));
      else setCreditValues(prev => ({ ...prev, [email]: "" }));

      alert(`Successfully processed ${type} of $${amountNum.toFixed(2)} for ${email}`);
    } catch (error) {
      console.error(`Error processing ${type}:`, error);
      alert("Failed to process transaction");
    }
  };

  const handleCloseInvestment = async (email: string) => {
    if (!window.confirm(`Are you sure you want to close investment for ${email}?`)) return;
    try {
      await axios.post(`${API_BASE}/api/invest/close/${email}`);
      fetchInvestments();
      alert("Investment closed successfully");
    } catch (error) {
      console.error("Error closing investment:", error);
      alert("Failed to close investment");
    }
  };

  return (
    <div className="max-w-7xl mx-auto p-4 md:p-8 space-y-6 bg-gray-50 min-h-screen">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900 tracking-tight">Active Investments</h1>
          <p className="text-sm text-gray-500 mt-1">Manage all active classic and basic investments</p>
        </div>

        <div className="relative w-full md:w-96">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
          <input
            type="text"
            placeholder="Search by email..."
            className="w-full pl-10 pr-4 py-3 bg-white border border-gray-200 rounded-xl shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all outline-none"
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setPagination(prev => ({ ...prev, page: 1 }));
            }}
          />
        </div>
      </div>

      <div className="flex flex-col gap-4">
        {loading ? (
          <div className="flex flex-col items-center justify-center p-12 bg-white rounded-2xl shadow-sm border border-gray-200">
            <Loader2 className="w-10 h-10 text-blue-500 animate-spin" />
            <p className="text-gray-500 font-medium mt-4">Fetching data...</p>
          </div>
        ) : investmentData.length === 0 ? (
          <div className="flex flex-col items-center justify-center p-12 bg-white rounded-2xl shadow-sm border border-gray-200">
            <Search className="w-12 h-12 text-gray-400 mb-4" />
            <p className="text-gray-500 font-medium">No results found</p>
          </div>
        ) : (
          investmentData.map((invest) => (
            <div key={invest._id} className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 transition-all">
              {/* Header */}
              <div className="flex justify-between items-start gap-3 mb-4">
                <div className="min-w-0 flex-1">
                  <h3 className="text-sm font-bold text-gray-900 truncate">{invest.email}</h3>
                  <div className="flex items-center gap-2 mt-0.5">
                    <span className="text-[10px] text-gray-400 font-medium uppercase tracking-tighter">{invest.subscription}</span>
                    <span className="w-1 h-1 bg-gray-300 rounded-full"></span>
                    <span className="text-[10px] text-green-600 font-bold uppercase tracking-tighter">Active</span>
                  </div>
                </div>
              </div>

              {/* Detail Grid */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-y-3 gap-x-4 text-xs mb-4">
                <div className="flex flex-col gap-0.5">
                  <span className="text-gray-400 font-bold uppercase text-[9px]">Entry Date</span>
                  <span className="text-gray-700 font-medium">{new Date(invest.createdAt).toLocaleDateString('en-GB')}</span>
                </div>
                <div className="flex flex-col gap-0.5">
                  <span className="text-gray-400 font-bold uppercase text-[9px]">Income Due</span>
                  <span className="text-gray-700 font-medium">{new Date(invest.incomeDate).toLocaleDateString('en-GB')}</span>
                </div>
                <div className="flex flex-col gap-0.5">
                  <span className="text-gray-400 font-bold uppercase text-[9px]">Investment</span>
                  <span className="text-blue-600 font-black text-sm">${parseFloat(invest.totalAmount).toLocaleString()}</span>
                </div>
              </div>

              {/* Compact Inline Actions */}
              <div className="space-y-3 pt-3 border-t border-gray-50">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {/* Debit Input */}
                  <div className="flex items-center gap-2">
                    <div className="relative flex-1">
                      <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 text-[10px] font-bold">$</span>
                      <input
                        type="text"
                        placeholder="Debit"
                        className="w-full pl-5 pr-2 py-2 bg-gray-50 border border-gray-100 rounded-xl text-xs focus:ring-1 focus:ring-red-500 outline-none"
                        value={debitValues[invest.email] || ""}
                        onChange={(e) => setDebitValues(prev => ({ ...prev, [invest.email]: e.target.value }))}
                      />
                    </div>
                    <button
                      onClick={() => handleTransaction(invest.email, debitValues[invest.email], invest.incomeDate, "debit")}
                      className="bg-red-500 text-white px-4 py-2 rounded-xl text-[10px] font-black uppercase tracking-wider"
                    >
                      Debit
                    </button>
                  </div>

                  {/* Credit Input */}
                  <div className="flex items-center gap-2">
                    <div className="relative flex-1">
                      <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 text-[10px] font-bold">$</span>
                      <input
                        type="text"
                        placeholder="Credit"
                        className="w-full pl-5 pr-2 py-2 bg-gray-50 border border-gray-100 rounded-xl text-xs focus:ring-1 focus:ring-green-500 outline-none"
                        value={creditValues[invest.email] || ""}
                        onChange={(e) => setCreditValues(prev => ({ ...prev, [invest.email]: e.target.value }))}
                      />
                    </div>
                    <button
                      onClick={() => handleTransaction(invest.email, creditValues[invest.email], invest.incomeDate, "credit")}
                      className="bg-green-500 text-white px-4 py-2 rounded-xl text-[10px] font-black uppercase tracking-wider"
                    >
                      Credit
                    </button>
                  </div>
                </div>

                <button
                  onClick={() => handleCloseInvestment(invest.email)}
                  className="w-full py-2 text-[9px] font-black uppercase tracking-widest text-slate-400 hover:text-red-500 transition-colors"
                >
                  Terminate Investment
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Pagination */}
      {!loading && investmentData.length > 0 && (
        <div className="p-4 md:px-6 md:py-4 mt-6 flex flex-col md:flex-row items-center justify-between gap-4 bg-white rounded-2xl shadow-sm border border-gray-200">
          <p className="text-xs text-gray-500 order-2 md:order-1 text-center font-medium">
            Showing <span className="text-gray-900 font-bold">{(pagination.page - 1) * pagination.limit + 1}</span> to{" "}
            <span className="text-gray-900 font-bold">
              {Math.min(pagination.page * pagination.limit, pagination.total)}
            </span>{" "}
            of <span className="text-gray-900 font-bold">{pagination.total}</span> entries
          </p>

          <div className="flex items-center gap-2 order-1 md:order-2">
            <span className="text-sm text-gray-600 hidden sm:inline">Show:</span>
            <select
              value={pagination.limit}
              onChange={(e) => {
                const newLimit = parseInt(e.target.value, 10);
                setPagination((prev) => ({ ...prev, limit: newLimit, page: 1 }));
              }}
              className="border border-gray-300 rounded-lg px-3 py-1 text-sm bg-white"
            >
              <option value="20">20 / page</option>
              <option value="50">50 / page</option>
              <option value="100">100 / page</option>
            </select>

            <button
              onClick={() => setPagination(prev => ({ ...prev, page: Math.max(1, prev.page - 1) }))}
              disabled={pagination.page === 1}
              className="p-2 border border-gray-100 rounded-xl bg-white hover:bg-gray-50 disabled:opacity-50"
            >
              <ChevronLeft className="w-5 h-5 text-gray-600" />
            </button>

            <div className="flex items-center gap-1.5">
              {[...Array(pagination.totalPages)].map((_, i) => {
                const p = i + 1;
                if (p === 1 || p === pagination.totalPages || Math.abs(p - pagination.page) <= 1) {
                  return (
                    <button
                      key={p}
                      onClick={() => setPagination(prev => ({ ...prev, page: p }))}
                      className={`w-9 h-9 text-xs font-bold rounded-xl transition-all ${pagination.page === p
                        ? "bg-blue-600 text-white"
                        : "bg-white text-gray-600 border border-gray-100"
                        }`}
                    >
                      {p}
                    </button>
                  );
                }
                if (Math.abs(p - pagination.page) === 2) {
                  return <span key={p} className="text-gray-400 text-xs px-1">...</span>;
                }
                return null;
              })}
            </div>

            <button
              onClick={() => setPagination(prev => ({ ...prev, page: Math.min(pagination.totalPages, prev.page + 1) }))}
              disabled={pagination.page === pagination.totalPages}
              className="p-2 border border-gray-100 rounded-xl bg-white hover:bg-gray-50 disabled:opacity-50"
            >
              <ChevronRight className="w-5 h-5 text-gray-600" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Invest;
